import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ExerciseType, HoroscopeData, ThemeDiscoveryData, WordsData, VisionData, LegacyVisionData, ExtendedHoroscopeReading, PhotoMemoriesData } from '../types'
import { isNewVisionFormat } from '../types'
import { debouncedSaveJourney } from '../services/api'
import { useUserStore } from './useUserStore'

interface ExerciseState {
  completedExercises: ExerciseType[]
  horoscope: HoroscopeData | null
  theme: ThemeDiscoveryData | null
  vision: VisionData | null
  photos: PhotoMemoriesData | null

  // Legacy alias for backwards compatibility
  words: ThemeDiscoveryData | null

  // Actions
  setHoroscopeData: (data: HoroscopeData) => void
  setExtendedHoroscopeReading: (reading: ExtendedHoroscopeReading) => void
  setThemeData: (data: ThemeDiscoveryData) => void
  setWordsData: (data: ThemeDiscoveryData) => void // Legacy alias
  setVisionData: (data: VisionData) => void
  setPhotosData: (data: PhotoMemoriesData) => void
  markComplete: (exercise: ExerciseType) => void
  isComplete: (exercise: ExerciseType) => boolean
  canProceed: () => boolean
  hasHoroscopeSignsEntered: () => boolean
  reset: () => void
  syncToDatabase: () => void

  // Sync functions for D1
  loadFromServer: (data: {
    horoscope?: HoroscopeData | null
    theme?: ThemeDiscoveryData | null
    words?: WordsData | null // Can be ThemeDiscoveryData or LegacyWordsData
    vision?: VisionData | LegacyVisionData | null
    photos?: PhotoMemoriesData | null
    completedExercises?: ExerciseType[]
  }) => void
  getSnapshot: () => {
    horoscope: HoroscopeData | null
    theme: ThemeDiscoveryData | null
    vision: VisionData | null
    photos: PhotoMemoriesData | null
    completedExercises: ExerciseType[]
  }
}

const initialState = {
  completedExercises: [] as ExerciseType[],
  horoscope: null,
  theme: null,
  words: null, // Legacy alias - points to theme
  vision: null,
  photos: null,
}

export const useExerciseStore = create<ExerciseState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setHoroscopeData: (data) => set({ horoscope: data }),

      setExtendedHoroscopeReading: (reading) => set((state) => ({
        horoscope: state.horoscope
          ? {
              ...state.horoscope,
              extendedReading: reading,
              insights: reading.overallTheme,
              themes: reading.keyEvents.map(e => e.event).slice(0, 5)
            }
          : null
      })),

      setThemeData: (data) => set({ theme: data, words: data }),
      setWordsData: (data) => set({ theme: data, words: data }), // Legacy alias
      setVisionData: (data) => set({ vision: data }),
      setPhotosData: (data) => set({ photos: data }),

      markComplete: (exercise) => {
        set((state) => ({
          completedExercises: state.completedExercises.includes(exercise)
            ? state.completedExercises
            : [...state.completedExercises, exercise],
        }))
        // Sync to database after marking complete
        get().syncToDatabase()
      },

      isComplete: (exercise) => get().completedExercises.includes(exercise),

      canProceed: () => get().completedExercises.length >= 2,

      hasHoroscopeSignsEntered: () => get().horoscope?.sun !== undefined,

      reset: () => set(initialState),

      // Sync current state to database
      syncToDatabase: () => {
        try {
          const { userId } = useUserStore.getState()
          const year = new Date().getFullYear()

          if (!userId) {
            console.warn('syncToDatabase skipped: no userId')
            return
          }

          const snapshot = get().getSnapshot()
          // Note: API expects 'words' not 'theme' for the theme discovery data
          debouncedSaveJourney(userId, year, {
            horoscope: snapshot.horoscope,
            words: snapshot.theme,
            vision: snapshot.vision,
            photos: snapshot.photos,
            completedExercises: snapshot.completedExercises,
          })
        } catch (error) {
          console.warn('Failed to sync exercises to database:', error)
        }
      },

      // Load data from server (used during initial sync)
      loadFromServer: (data) => {
        // Support both new theme discovery format and legacy words format
        // Only use as theme if it has energizedBy (indicating new format)
        let themeData: ThemeDiscoveryData | null = null
        if (data.theme) {
          themeData = data.theme
        } else if (data.words && 'energizedBy' in data.words) {
          // It's new ThemeDiscoveryData stored in legacy words field
          themeData = data.words as ThemeDiscoveryData
        }
        // Legacy words data (with finalTheme) is ignored for theme - users should redo theme discovery

        // Support both new interactive vision format and legacy text-based format
        // Only use new format if it has focusAreas or caughtDreams (indicating new format)
        // Legacy vision data (with responses array) is ignored - users should redo vision
        let visionData: VisionData | null = null
        if (data.vision && isNewVisionFormat(data.vision)) {
          visionData = data.vision
        }

        set({
          horoscope: data.horoscope ?? null,
          theme: themeData,
          words: themeData, // Keep legacy alias in sync
          vision: visionData,
          photos: data.photos ?? null,
          completedExercises: data.completedExercises ?? [],
        })
      },

      // Get current state for syncing to server
      getSnapshot: () => {
        const state = get()
        return {
          horoscope: state.horoscope,
          theme: state.theme,
          vision: state.vision,
          photos: state.photos,
          completedExercises: state.completedExercises,
        }
      },
    }),
    {
      name: `year-wrapped-exercises-${new Date().getFullYear()}`,
    }
  )
)
